import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Download, Info, RefreshCw, X } from 'lucide-react';
import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
import {
  blobErrorMessage,
  uniformDashboardAPI,
  uniformDownloadFileName,
  type UniformDownloadKind,
  type UniformTabKey,
} from '../../services/api';
import { downloadXlsx } from '../../utils/download';
import { SearchBox } from './DashboardPrimitives';
import { InlineLoader } from '../loading';
import { UNIFORM_STAGE_SLUG, uniformStageFromSlug } from './lists/uniform';
import { useDashboardList } from './useDashboardList';
import { SortTh, useTableSort, type SortAccessors, type SortDir } from './useTableSort';
import UniformCourierStatusModal from './UniformCourierStatusModal';
import UniformKitModal from './UniformKitModal';
import {
  DEFAULT_UNIFORM_TAB,
  KIT_STOCK_HINT,
  UNIFORM_TABS,
  classifyFailure,
  codeName,
  countLabel,
  emptyState,
  fmtInt,
  fmtYmd,
  httpStatus,
  matchesUniformSearch,
  monthName,
  readKitStock,
  readRequests,
  readSla,
  rowShortfall,
  shortfallText,
  slaHint,
  slaPct,
  slaValue,
  statusBadge,
  stockValue,
  tatBadge,
  type BadgeTone,
  type UniformCounts,
  type UniformIntakeLink,
  type UniformKitStock,
  type UniformLoadFailure,
  type UniformRequestRow,
  type UniformRequestsData,
  type UniformSla,
} from './uniformDashboard';

/**
 * Dashboard → Uniform & Asset Management tab.
 *
 * 1Office Admin Management's Uniform Dashboard for the project selected in
 * ChannelPlex: uniform requests by stage — Joined (an employee) and Pre Joinee (a
 * candidate, by Join ID) — in five sub-tabs, this month's SLA counts, the stock of
 * the project's kits, three Excel downloads and three row actions. It is a port of
 * Project Management's "Uniform Report", which already reads the same upstream
 * routes; ChannelPlex stores none of this and changes none of it.
 *
 * Things a reader must know before touching it:
 *   1. ACCESS IS THE API'S ANSWER — FROM ANY CALL. The `web.uniform_dashboard` grant
 *      is checked on the server against the project in the URL; the browser cannot
 *      evaluate it. So a 403 is a STATE of this tab — an in-tab "access needed" panel
 *      carrying the server's own message — not an error banner and not a logout. That
 *      holds for every call, not only the loads: a 403 on a download, Copy intake
 *      link, View kit or a courier lookup means the grant went while the tab was
 *      open, so it closes any modal, takes the project's rows off the screen and
 *      shows the same panel (enterGate). Likewise a 503 carrying the API's envelope
 *      on a load means the server has no Admin Management secret: "not set up",
 *      nobody's fault here.
 *   2. COUNTS ARE NOT ROW COUNTS. Admin Management returns true totals in `counts`
 *      but caps the rows (newest 5,000 open, newest 5,000 Completed). Every number
 *      on the sub-tab strip comes from `counts`; rows.length is only ever compared
 *      against it — to say "you are not seeing everything" whenever it is the
 *      smaller, and so that an empty table under a non-zero count is never worded
 *      as "no request is waiting".
 *   3. ONE PROJECT AT A TIME, AND NEVER THE LAST ONE'S. The project can change under
 *      this tab while requests are in flight. `loadSeq` lets only the newest load
 *      land, and a load for a new project starts by taking the old one's rows,
 *      counts and card figures off the screen — a failed load for project B must
 *      not leave project A's standing under a banner, where they would read as B's.
 *      (A Refresh drops the rows too, and whatever part of it then fails.)
 *   4. THE THREE PARTS LOAD APART AND FAIL APART. Rows, SLA counts and kit stock are
 *      three calls, and each goes on screen when its OWN answer arrives: a slow kit
 *      stock call must not hold the table at "Loading…" (the API allows Admin
 *      Management 30 seconds per call). A failed card says so in the card and never
 *      blanks the table, and the other way round. Refresh follows the rows call alone.
 *   5. NEITHER THE MONTH PICKER NOR THE FILTERS APPLY. The SLA card is always the
 *      current India-time month (upstream takes no month), and the rows are 1Office
 *      requests, not ChannelPlex employees the dashboard's attribute filters could
 *      narrow. Like Onboarding, the tab takes only `projectId`.
 *   6. ONE SNAPSHOT ON SCREEN. The API holds one upstream answer per project for a
 *      minute, and every rows answer says when that answer was fetched (`fetchedAt`).
 *      The sub-tabs load one at a time, so two of them CAN come from different
 *      snapshots — once the minute has lapsed, or around a Refresh — and a request
 *      that changed stage in between would then be listed under both, under counts
 *      that match neither. So a rows answer from a different snapshot than the one
 *      held drops the other sub-tabs' rows (landRows), and a sub-tab opened while a
 *      Refresh is in flight asks with `refresh`, so the API joins that Refresh
 *      instead of answering from the snapshot it is about to replace.
 *
 *   7. THE OPEN STAGE IS THE ADDRESS. Which sub-tab is open is read from ?list=<slug>
 *      on every render (useDashboardList), so the home screen, page search and a
 *      colleague's link can land on a stage, and the strip opens one the same way. No
 *      slug, or one that is not a stage, is the first stage.
 *
 * Rows load per sub-tab — one request each, kept until Refresh, a project change or
 * a newer snapshot. That is cheap: the API holds the whole upstream answer for a
 * minute, so the five sub-tabs cost one upstream call between them.
 */

// What the access panel says when the 403 carried no message of its own (a proxy's
// page, an older API). The API's message is shown when there is one: it tells "no
// grant on this project" apart from "could not verify who you are".
const ACCESS_NEEDED = 'You don\'t have access to Uniform & Asset Management for this project. Ask an '
  + 'administrator to tick "Uniform & Asset Management" for your role in Access Management.';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Stable identity for "no rows", so the table's memoised filter and its page reset
 *  don't re-run on every render while a sub-tab is loading. */
const NO_ROWS: UniformRequestRow[] = [];

type Gate = { kind: 'ok' } | Exclude<UniformLoadFailure, { kind: 'error' }>;
/** A failure that answers for the whole tab: the access panel, or "not set up". */
type Blocked = Exclude<Gate, { kind: 'ok' }>;
const GATE_OK: Gate = { kind: 'ok' };

const DOWNLOAD_WHAT: Record<UniformDownloadKind, string> = {
  report: 'uniform report',
  'kit-stock': 'kit stock',
  'kit-report': 'kit report',
};

/** The API's `fetchedAt` as a local clock time. This one IS an instant (an ISO
 *  timestamp with a zone), so Date is the right tool — unlike the request dates,
 *  which are calendar days in India time and are formatted as text. */
function fmtSnapshotTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
}

/** What the dashboard holds and where it is read from. The hover text of "As of": it used
 *  to be a paragraph at the top of the tab. */
const UNIFORM_SOURCE_HINT = 'Uniform requests by stage — Joined (employee) and Pre Joinee (candidate, by Join ID) — for this project, '
  + 'read from 1Office Admin Management. Month and filters do not apply here.';

const BADGE_TONE: Record<BadgeTone, string> = {
  success: 'bg-emerald-50 text-emerald-700',
  danger: 'bg-rose-50 text-rose-700',
  neutral: 'bg-gray-100 text-gray-600',
};

function Badge({ text, tone, title }: { text: string; tone: BadgeTone; title?: string }) {
  return (
    <span
      title={title}
      data-tone={tone}
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${BADGE_TONE[tone]}`}
    >
      {text}
    </span>
  );
}

/** A card figure's colour: its own when it is a number, muted when it is a
 *  placeholder. A dash drawn in the Out-of-stock red reads as an alarm about
 *  nothing; "no figure" should look like no figure. */
const figureTone = (value: string, tone: string): string => (value === '—' || value === '…' ? 'text-gray-400' : tone);

const PILL = 'inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-primary-200 bg-white px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50';
// The SLA and Kit stock tiles are drawn by hand (each holds more than one figure), in the
// look of the dashboards' headline tiles (StatTile): the shared white frame, a grey label,
// the number large and bold, and a short grey line under it.
const CARD = 'rounded-2xl border border-gray-200 bg-white p-4 shadow-sm';
const TILE_LABEL = 'text-sm font-medium text-gray-500';
const TILE_NUMBER = 'text-3xl font-bold tabular-nums tracking-tight';

// ── The requests table ───────────────────────────────────────────────────────

const TH = 'whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500';
// Padding apart from colour: Tailwind emits text colours alphabetically, so two on
// one element do not resolve in the order they are written. A cell with its own
// colour takes CELL, never TD.
const CELL = 'px-4 py-3';
const TD = `${CELL} text-gray-700`;

/* Raw values, not the display strings: 'YYYY-MM-DD' orders chronologically as text,
   which 'dd-mm-yy' does not. A cell that shows nothing (no badge, no name) returns
   null so it sinks to the bottom in both directions instead of sorting as a dash. */
const SORT: SortAccessors<UniformRequestRow> = {
  code: (r) => r.request_code,
  person: (r) => {
    const v = codeName(r.person_code, r.person_name);
    return v === '—' ? null : v;
  },
  budget: (r) => r.budget_name,
  created: (r) => r.created_on,
  submitted: (r) => r.submitted_on,
  processed: (r) => r.processed_on,
  status: (r) => statusBadge(r)?.text ?? null,
  tat: (r) => tatBadge(r)?.text ?? null,
};

/** A first click reads A→Z on the text columns and newest-first on the dates. */
const DATE_KEYS = new Set(['created', 'submitted', 'processed']);
const firstSortDir = (key: string): SortDir => (DATE_KEYS.has(key) ? 'desc' : 'asc');

/**
 * One sub-tab's table: search box, sortable headers, client-side pages.
 *
 * Its own component, mounted with a key of project + sub-tab, so the search text,
 * the sort and the page all start fresh on a sub-tab switch — a search for a name on
 * Completed must not silently hide every row of Courier In Process.
 *
 * The header row and the body rows list their conditional cells in the SAME order
 * with the same conditions. That is deliberate: the table-actions source checker
 * finds the Actions column by position, so a cell reordered in one row but not the
 * other would move the check off the column it is meant to guard.
 */
function UniformRequestsTable({
  tab,
  rows,
  total,
  loading,
  failed,
  toolbar,
  linkBusy,
  onViewKit,
  onCopyLink,
  onTrack,
}: {
  tab: UniformTabKey;
  rows: UniformRequestRow[];
  /** This sub-tab's TRUE total, from `counts` — not rows.length. It decides what an
   *  empty table says: "no request is waiting" only when this is 0. */
  total: number | null;
  loading: boolean;
  /** The sub-tab's load failed: say so, rather than claim "no requests". */
  failed: boolean;
  toolbar: ReactNode;
  /** Requests whose intake link is being fetched — only those rows' buttons disable. */
  linkBusy: ReadonlySet<number>;
  onViewKit: (r: UniformRequestRow) => void;
  onCopyLink: (r: UniformRequestRow) => void;
  onTrack: (r: UniformRequestRow) => void;
}) {
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => rows.filter((r) => matchesUniformSearch(r, search)), [rows, search]);
  const sort = useTableSort(filtered, SORT, { firstDir: firstSortDir });

  // Back to the first page whenever what is listed, or its order, changes.
  useEffect(() => { setPage(1); }, [rows, search, pageSize, sort.sortKey, sort.sortDir]);

  // What is LISTED (after the search), as opposed to `total`, the sub-tab's true count.
  const listed = sort.sorted.length;
  const totalPages = Math.max(1, Math.ceil(listed / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageRows = sort.sorted.slice(pageStart, pageStart + pageSize);

  /* Which columns this sub-tab has, after the four every sub-tab shares. The Project
     column of the 1Office screen is left out on purpose: this tab is one project. */
  const cols = {
    // Completed: what the request ended as — it lists cancelled and rejected too.
    status: tab === 'completed',
    submitted: tab === 'submitted',
    processed: tab === 'in_process' || tab === 'completed',
    tat: tab === 'submitted' || tab === 'in_process' || tab === 'completed',
    actions: tab === 'ack_pending' || tab === 'approval_pending' || tab === 'in_process',
  };
  const colCount = 4 + Object.values(cols).filter(Boolean).length;
  const empty = emptyState(tab, total);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="w-full max-w-sm">
          <SearchBox value={search} onChange={setSearch} placeholder="Search request code, employee, join ID, budget…" />
        </div>
        <div className="flex flex-wrap items-center gap-2">{toolbar}</div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* One scroll box for both axes, so the header can stick to its top: a page of
            100 rows keeps its column names in view. The header background is solid —
            a translucent one lets the rows show through as they pass under it. */}
        <div className="max-h-[65vh] overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 text-left">
              <tr>
                <SortTh label="Request Code" sortKey="code" sort={sort} className={TH} />
                <SortTh
                  label="Employee Code-Name (Join ID-Name)"
                  sortKey="person"
                  sort={sort}
                  className={TH}
                  title="Joined: employee code-name. Pre Joinee: Join ID-candidate name (no employee code yet)."
                />
                <SortTh label="Budget Name" sortKey="budget" sort={sort} className={TH} />
                <SortTh label="Created Date" sortKey="created" sort={sort} className={TH} />
                {cols.status && <SortTh label="Status" sortKey="status" sort={sort} className={TH} />}
                {cols.submitted && <SortTh label="User Submitted" sortKey="submitted" sort={sort} className={TH} />}
                {cols.processed && (
                  <SortTh
                    label={tab === 'completed' ? 'Request Processed' : 'Request Processed On'}
                    sortKey="processed"
                    sort={sort}
                    className={TH}
                    title={tab === 'completed'
                      ? 'The day the kit went out — or, for a cancelled or rejected request, the day it ended'
                      : undefined}
                  />
                )}
                {cols.tat && (
                  <SortTh
                    label="TAT (3 days)"
                    sortKey="tat"
                    sort={sort}
                    className={TH}
                    title="Judged by 1Office from a recorded start — the user's submit, or the PM approval. A request with neither carries no label."
                  />
                )}
                {cols.actions && (
                  <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold text-gray-500">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr><td colSpan={colCount} className="px-4 py-10 text-center text-gray-400"><InlineLoader label="Loading…" className="flex justify-center" /></td></tr>
              ) : failed ? (
                // Not the empty wording: "no request is waiting" would be a claim
                // about 1Office that a failed load cannot make.
                <tr>
                  <td colSpan={colCount} className="px-4 py-10 text-center text-sm text-gray-500">
                    These requests could not be loaded. Use Refresh to try again.
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-10 text-center">
                    {search.trim() && rows.length > 0 ? (
                      <p className="text-sm text-gray-500">No request matches your search.</p>
                    ) : (
                      // By the sub-tab's true total, never by rows.length: no rows
                      // under a non-zero count is "not sent", not "nothing waiting".
                      <>
                        <p className="text-sm font-medium text-gray-700">{empty.title}</p>
                        <p className="mt-0.5 text-sm text-gray-500">{empty.text}</p>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                pageRows.map((r) => {
                  const tat = tatBadge(r);
                  const ended = statusBadge(r);
                  return (
                    <tr key={r.request_id} className="hover:bg-gray-50">
                      <td className={`${CELL} whitespace-nowrap font-medium text-gray-900`}>{r.request_code}</td>
                      <td className={TD}>
                        <div>{codeName(r.person_code, r.person_name)}</div>
                        {/* The strip counts candidates and employees apart, and a Join
                            ID looks just like an employee code — so say which this is. */}
                        {r.request_type === -11 && <div className="text-[11px] text-gray-400">Pre Joinee</div>}
                      </td>
                      <td className={TD}>{r.budget_name || '—'}</td>
                      <td className={`${TD} whitespace-nowrap`}>{fmtYmd(r.created_on)}</td>
                      {cols.status && (
                        <td className={TD}>{ended && <Badge text={ended.text} tone={ended.tone} />}</td>
                      )}
                      {cols.submitted && <td className={`${TD} whitespace-nowrap`}>{fmtYmd(r.submitted_on)}</td>}
                      {cols.processed && <td className={`${TD} whitespace-nowrap`}>{fmtYmd(r.processed_on)}</td>}
                      {cols.tat && (
                        <td className={TD}>{tat && <Badge text={tat.text} tone={tat.tone} title={tat.hint} />}</td>
                      )}
                      {cols.actions && (
                        <td className="px-4 py-3 text-center">
                          <TableActionGroup>
                            {tab === 'in_process' ? (
                              <TableActionButton
                                action="track"
                                label="View courier status"
                                title="View courier status — where the parcel is"
                                onClick={() => onTrack(r)}
                              />
                            ) : (
                              <TableActionButton
                                action="view"
                                label="View kit"
                                title="View kit — the garments, sizes and quantities on this request"
                                onClick={() => onViewKit(r)}
                              />
                            )}
                            {/* Only a Pre Joinee candidate has an intake form to link to;
                                a Joined employee fills their sizes in the app. Without a
                                Join ID there is no candidate to build the link for. */}
                            {tab === 'ack_pending' && r.request_type === -11 && r.join_id != null && (
                              <TableActionButton
                                action="link"
                                label="Copy intake link"
                                title="Copy intake link — the candidate's size form. Nothing is mailed."
                                disabled={linkBusy.has(r.request_id)}
                                onClick={() => onCopyLink(r)}
                              />
                            )}
                          </TableActionGroup>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {listed > 0 && !loading && !failed && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/40 px-4 py-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded-md border border-gray-200 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Showing {fmtInt(pageStart + 1)}–{fmtInt(Math.min(pageStart + pageSize, listed))} of {fmtInt(listed)}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage(Math.max(1, safePage - 1))}
                  disabled={safePage <= 1}
                  className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">Page {safePage} of {totalPages}</span>
                <button
                  type="button"
                  onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                  disabled={safePage >= totalPages}
                  className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── The tab ──────────────────────────────────────────────────────────────────

export default function UniformTab({ projectId }: { projectId: number | null }) {
  const { list: listSlug, openList } = useDashboardList();
  const activeTab: UniformTabKey = uniformStageFromSlug(listSlug) ?? DEFAULT_UNIFORM_TAB;
  const [gate, setGate] = useState<Gate>(GATE_OK);

  // Rows per sub-tab, as loaded — kept until Refresh, a project change, or an answer
  // from a newer snapshot (landRows). A sub-tab with neither rows nor an error here
  // is, by definition, still loading: there is no separate "loading" flag to fall
  // out of step with them.
  const [snapshots, setSnapshots] = useState<Partial<Record<UniformTabKey, UniformRequestsData>>>({});
  const [tabErrors, setTabErrors] = useState<Partial<Record<UniformTabKey, string>>>({});
  // The newest rows answer: the five sub-tabs' true totals, and when the API fetched
  // them. Every rows answer carries all five counts, so the newest answer wins
  // whichever sub-tab it was for. The "As of" time is held WITH the counts, in one
  // value, so the time on screen is always the time of the numbers on screen.
  const [latest, setLatest] = useState<{ counts: UniformCounts; fetchedAt: string | null } | null>(null);
  const counts = latest?.counts ?? null;
  // The rows call of a full load (first open, project change, Refresh) is in flight.
  // The two cards have their own flags: they are not what Refresh waits for.
  const [loadingAll, setLoadingAll] = useState(false);

  const [sla, setSla] = useState<UniformSla | null>(null);
  const [slaLoading, setSlaLoading] = useState(true);
  const [slaError, setSlaError] = useState<string | null>(null);
  const [kitStock, setKitStock] = useState<UniformKitStock | null>(null);
  const [kitStockLoading, setKitStockLoading] = useState(true);
  const [kitStockError, setKitStockError] = useState<string | null>(null);

  // A failed download or intake link — an action, not a load, so it has its own
  // banner and never touches the rows. (A 403 is the exception: see enterGate.)
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyDownloads, setBusyDownloads] = useState<Partial<Record<UniformDownloadKind, boolean>>>({});
  const [linkBusy, setLinkBusy] = useState<ReadonlySet<number>>(() => new Set());
  const [viewRow, setViewRow] = useState<UniformRequestRow | null>(null);
  const [trackRow, setTrackRow] = useState<UniformRequestRow | null>(null);

  // Only the newest load may land: a slow answer for an earlier project (or from
  // before a Refresh) must not overwrite a later one. Bumped by every full load.
  const loadSeq = useRef(0);
  // Sub-tabs with a rows request in flight under the current loadSeq, so clicking a
  // sub-tab twice while it loads asks once.
  const inflight = useRef<Set<UniformTabKey>>(new Set());
  // The snapshot generation on screen: the `fetchedAt` its rows share, and which
  // sub-tabs hold rows from it. null = no rows held. It mirrors `snapshots`, in a
  // ref because an answer has to be judged against it the moment it arrives, not a
  // render later.
  const held = useRef<{ fetchedAt: string | null; tabs: Set<UniformTabKey> } | null>(null);
  // A Refresh's rows call is in flight. Until it settles the API's snapshot is the
  // one being replaced, so a sub-tab opened meanwhile must not be answered from it.
  const refreshing = useRef(false);
  // The access (or "not set up") panel is up. In a ref for the same reason as
  // `held`: two answers of one load can settle back to back, and the second must
  // already see that the first one closed the tab.
  const gated = useRef(false);
  // Read through refs by the async handlers, which outlive the render they started in.
  const projectRef = useRef(projectId);
  projectRef.current = projectId;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  /** Forget every sub-tab's rows, and with them the generation they belonged to. */
  const dropRows = useCallback(() => {
    held.current = null;
    setSnapshots({});
  }, []);

  /** Take everything 1Office-sourced off the screen. */
  const clearData = useCallback(() => {
    dropRows();
    setTabErrors({});
    setLatest(null);
    setSla(null);
    setKitStock(null);
  }, [dropRows]);

  /**
   * The access answer (403) or "not set up" (the API's own 503), from WHICHEVER call
   * brought it — a load, a download, the intake link, or a lookup inside a modal.
   *
   * It is the same answer for all of them, because they sit behind one middleware:
   * on a first load the reader never had the grant, and on anything later it went
   * while the tab was open. So any open modal closes, everything the project put on
   * screen goes — rows, counts, card figures, and a notice that may be carrying a
   * candidate's intake link — and the panel takes the tab's place. Nothing of the
   * project may be left standing beside "you don't have access".
   *
   * A 403 outranks "not set up": a user without the grant has no business learning
   * how the server is configured, so once the access panel is up a later 503 does
   * not replace it.
   */
  const enterGate = useCallback((failure: Blocked) => {
    gated.current = true;
    setGate((g) => (g.kind === 'denied' ? g : failure));
    setViewRow(null);
    setTrackRow(null);
    setNotice(null);
    setActionError(null);
    clearData();
  }, [clearData]);

  /**
   * Put one sub-tab's rows on screen, keeping ONE snapshot generation there.
   *
   * Counts and "As of" are always the newest answer's. If that answer was fetched at
   * a different time than the rows already held, those rows are from another
   * snapshot: a request that changed stage between the two would be listed under
   * both, and their sub-tabs would disagree with the counts just put above them. So
   * they are dropped, and reload from the same snapshot when next opened.
   *
   * Returns true when the sub-tab the user is LOOKING at was among the dropped —
   * nobody is going to "next open" that one, so the caller asks for it again.
   */
  const landRows = useCallback((tab: UniformTabKey, data: UniformRequestsData): boolean => {
    const before = held.current;
    const moved = before !== null && before.fetchedAt !== data.fetchedAt;
    held.current = {
      fetchedAt: data.fetchedAt,
      tabs: before && !moved ? new Set(before.tabs).add(tab) : new Set([tab]),
    };
    setLatest({ counts: data.counts, fetchedAt: data.fetchedAt });
    setSnapshots((prev) => (moved ? { [tab]: data } : { ...prev, [tab]: data }));
    const active = activeTabRef.current;
    return moved && active !== tab && before.tabs.has(active);
  }, []);

  /**
   * Ask for one sub-tab's rows and land the answer, under the load on screen.
   *
   * `lead` marks the rows call of a full load (loadAll has already entered it in
   * `inflight`): its answer, either way, is what ends the "Loading…" of the tab and
   * the Refresh window. Any other call is one more sub-tab under that load, and asks
   * once however often the sub-tab is clicked.
   *
   * `refresh` is not a parameter: it is whether a Refresh is in flight right now.
   * For the Refresh's own rows call that is the Refresh. For a sub-tab opened
   * meanwhile it makes the API join that Refresh — one upstream call between them —
   * where a plain read could be answered from the snapshot being replaced, and
   * would then sit on screen, stale, beside the refreshed counts.
   */
  const fetchRows = useCallback(async (pid: number, tab: UniformTabKey, lead: boolean): Promise<void> => {
    if (!lead) {
      if (inflight.current.has(tab)) return;
      inflight.current.add(tab);
      setTabErrors((prev) => {
        if (!(tab in prev)) return prev;
        const next = { ...prev };
        delete next[tab];
        return next;
      });
    }
    const seq = loadSeq.current;
    /** This call's answer arrived, and it is still the load on screen. */
    const settle = () => {
      inflight.current.delete(tab);
      if (lead) {
        refreshing.current = false;
        setLoadingAll(false);
      }
    };
    let dropped = false;
    try {
      const res = await uniformDashboardAPI.getRequests({ projectId: pid, tab, refresh: refreshing.current });
      // A newer full load has replaced the set; its entries are not this call's to touch.
      if (seq !== loadSeq.current) return;
      settle();
      // The panel went up while this was on its way: nothing lands behind it.
      if (gated.current) return;
      dropped = landRows(tab, readRequests(res.data?.data, tab));
    } catch (err) {
      if (seq !== loadSeq.current) return;
      settle();
      const failure = classifyFailure(err, 'Failed to load uniform requests.');
      if (failure.kind !== 'error') {
        enterGate(failure);
        return;
      }
      if (gated.current) return;
      // The counts go with the rows of a full load: after a failed Refresh the old
      // ones would be presented as current. Unless another sub-tab has landed since
      // this load began — then the counts on the strip are that answer's, and belong
      // to the rows on screen.
      if (lead && !held.current) setLatest(null);
      setTabErrors((prev) => ({ ...prev, [tab]: failure.message }));
    }
    if (dropped) void fetchRows(pid, activeTabRef.current, false);
  }, [enterGate, landRows]);

  /**
   * A full load: this sub-tab's rows, the SLA counts and the kit stock.
   *
   * Three calls, started together and each put on screen when its OWN answer arrives
   * — never held for the slowest. All three are guarded by the same `seq`, and a 403
   * from any of them turns the tab into the access panel the moment it arrives.
   *
   * `refresh` asks the API to bypass and replace its one-minute snapshot. On a
   * Refresh the strip keeps its last counts until the new ones land (same project,
   * so they are merely a minute old); on a project change `fresh` drops them first,
   * because the last project's counts under this project's name would be wrong, not
   * old. Cached rows of every sub-tab go either way.
   */
  const loadAll = useCallback((pid: number, tab: UniformTabKey, opts: { refresh: boolean; fresh: boolean }) => {
    const seq = ++loadSeq.current;
    inflight.current = new Set([tab]);
    refreshing.current = opts.refresh;
    gated.current = false;
    setGate(GATE_OK);
    dropRows();
    setTabErrors({});
    if (opts.fresh) {
      setLatest(null);
      setSla(null);
      setKitStock(null);
    }
    setLoadingAll(true);
    setSlaLoading(true);
    setSlaError(null);
    setKitStockLoading(true);
    setKitStockError(null);

    void fetchRows(pid, tab, true);

    /**
     * One card. A card that fails says so in the card and touches nothing else —
     * unless the failure is the access answer, which is the whole tab's.
     */
    const loadCard = async <T,>(
      call: Promise<{ data?: { data?: unknown } }>,
      read: (raw: unknown) => T | null,
      put: (value: T | null) => void,
      fail: (message: string) => void,
      done: () => void,
    ) => {
      try {
        const res = await call;
        if (seq !== loadSeq.current || gated.current) return;
        put(read(res.data?.data));
      } catch (err) {
        if (seq !== loadSeq.current) return;
        const failure = classifyFailure(err, 'Failed to load');
        if (failure.kind !== 'error') {
          enterGate(failure);
          return;
        }
        if (gated.current) return;
        put(null); // never leave another project's figures on screen under an error
        fail(failure.message);
      } finally {
        if (seq === loadSeq.current) done();
      }
    };
    void loadCard(
      uniformDashboardAPI.getKpi({ projectId: pid, refresh: opts.refresh }),
      readSla, setSla, setSlaError, () => setSlaLoading(false),
    );
    void loadCard(
      uniformDashboardAPI.getKitStock({ projectId: pid, refresh: opts.refresh }),
      readKitStock, setKitStock, setKitStockError, () => setKitStockLoading(false),
    );
  }, [dropRows, enterGate, fetchRows]);

  // A project change is a new screen: close what was open, forget what was said,
  // and load again. The sub-tab the user was on is kept — someone comparing
  // Completed across projects should not be thrown back to the first sub-tab.
  useEffect(() => {
    setViewRow(null);
    setTrackRow(null);
    setNotice(null);
    setActionError(null);
    setBusyDownloads({});
    setLinkBusy(new Set());
    if (projectId == null) {
      loadSeq.current += 1;
      inflight.current = new Set();
      refreshing.current = false;
      gated.current = false;
      setGate(GATE_OK);
      clearData();
      setLoadingAll(false);
      return undefined;
    }
    loadAll(projectId, activeTabRef.current, { refresh: false, fresh: true });
    // Unmounting (or moving to another project) orphans whatever is in flight.
    return () => { loadSeq.current += 1; };
  }, [projectId, loadAll, clearData]);

  /** What opening a sub-tab does, whichever way it was opened. */
  const enterTab = (tab: UniformTabKey) => {
    setNotice(null);
    // Rows are fetched the first time a sub-tab is opened — and again if its last
    // attempt failed, which makes re-opening it the retry, or if a newer snapshot
    // has dropped the rows it held.
    if (projectId != null && gate.kind === 'ok' && !snapshots[tab]) void fetchRows(projectId, tab, false);
  };

  // The open sub-tab changed in the address: a press on the strip, or a link from the
  // home screen or page search while this dashboard is open.
  // (The sub-tab the dashboard OPENS on is asked for by the full load above.)
  const enteredTab = useRef(activeTab);
  useEffect(() => {
    if (enteredTab.current === activeTab) return;
    enteredTab.current = activeTab;
    enterTab(activeTab);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTab = (tab: UniformTabKey) => {
    // The sub-tab already open: nothing changes in the address, so its retry is run here.
    if (tab === activeTab) enterTab(tab);
    else openList(UNIFORM_STAGE_SLUG[tab]);
  };

  const refresh = () => {
    if (projectId == null) return;
    setNotice(null);
    setActionError(null);
    loadAll(projectId, activeTab, { refresh: true, fresh: false });
  };

  /** "Check again" / "Try again" on a panel: a first load of the project, from nothing. */
  const recheck = () => {
    if (projectId == null) return;
    setNotice(null);
    setActionError(null);
    loadAll(projectId, activeTab, { refresh: false, fresh: true });
  };

  /**
   * One of the three workbooks. The file is saved even if the project changed while
   * it was being built — the user asked for it — but a failure is only reported
   * while they are still looking at the project it was for.
   *
   * A 403 is not a failed download: it is the access answer (enterGate). Anything
   * else — including the API's 429 when too many workbooks are being built at once —
   * is shown with the server's message, and the rows stay.
   */
  const runDownload = useCallback(async (kind: UniformDownloadKind) => {
    if (projectId == null) return;
    const pid = projectId;
    const fallback = `Failed to download the ${DOWNLOAD_WHAT[kind]}.`;
    setBusyDownloads((prev) => ({ ...prev, [kind]: true }));
    setActionError(null);
    try {
      const res = await uniformDashboardAPI.download({ projectId: pid, kind });
      const blob = res.data as Blob;
      const type = String(res.headers?.['content-type'] || blob?.type || '');
      if (!type.includes('spreadsheetml.sheet')) {
        // A 2xx that is not a workbook — a proxy's HTML page, or an envelope sent
        // with the wrong status. Saving it as .xlsx would hand the user a file Excel
        // calls corrupt; read it for a message instead.
        const message = await blobErrorMessage({ response: { data: blob } }, fallback);
        if (projectRef.current === pid) setActionError(message);
        return;
      }
      downloadXlsx(uniformDownloadFileName(kind, res.headers?.['content-disposition']), blob);
    } catch (err) {
      // The request asked for a Blob, so the refusal's envelope arrives as one: its
      // message has to be read out before the 403's can go on the access panel.
      const message = await blobErrorMessage(err, '');
      if (projectRef.current !== pid) return;
      if (httpStatus(err) === 403) enterGate({ kind: 'denied', message: message || null });
      else setActionError(message || fallback);
    } finally {
      if (projectRef.current === pid) setBusyDownloads((prev) => ({ ...prev, [kind]: false }));
    }
  }, [projectId, enterGate]);

  /**
   * Copy a Pre Joinee candidate's intake link — the same link 1Office's Pre Joinee
   * Uniforms page copies: asking for it mails nobody and marks nothing sent. A
   * refused clipboard write (permission denied, an insecure origin, an older
   * browser) still leaves the user holding the link, because the notice then
   * carries the URL itself.
   */
  const copyLink = useCallback(async (row: UniformRequestRow) => {
    if (projectId == null || row.join_id == null) return;
    const pid = projectId;
    const who = row.person_name?.trim() || `join ${row.join_id}`;
    /** The screen the button was pressed on is gone: another project, or the access panel. */
    const left = () => projectRef.current !== pid || gated.current;
    setLinkBusy((prev) => new Set(prev).add(row.request_id));
    // Cleared when the action STARTS, not when it succeeds: a link that arrives late
    // must not wipe the error of a download that failed while it was on its way.
    setActionError(null);
    try {
      const res = await uniformDashboardAPI.getIntakeLink({ projectId: pid, requestId: row.request_id });
      const link = (res.data?.data as Partial<UniformIntakeLink> | undefined)?.link;
      if (typeof link !== 'string' || !link.trim()) throw new Error('The answer carried no link');
      // Checked BEFORE the clipboard is touched. The link lets whoever holds it fill
      // in a candidate's form, and the clipboard is not this tab's to overwrite once
      // the user has moved on: an answer that arrives late for a project they have
      // left would silently replace what they had copied, and their next paste —
      // into a chat, a mail — would be that link, with nothing on screen to say so.
      if (left()) return;
      let copied = true;
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        copied = false;
      }
      // And again after it — the write is asynchronous too. Not under another
      // project's heading: the name in the notice is a candidate of the project
      // that was on screen when the button was pressed.
      if (left()) return;
      setNotice(copied ? `Intake link copied — ${who}` : `Intake link for ${who}: ${link}`);
    } catch (err) {
      if (projectRef.current !== pid) return;
      const failure = classifyFailure(err, 'Could not build the link.');
      if (failure.kind === 'denied') enterGate(failure);
      else setActionError(`${row.request_code}: ${failure.message}`);
    } finally {
      if (projectRef.current === pid) {
        setLinkBusy((prev) => {
          const next = new Set(prev);
          next.delete(row.request_id);
          return next;
        });
      }
    }
  }, [projectId, enterGate]);

  if (projectId == null) {
    return <div className="flex h-40 items-center justify-center text-sm text-gray-500">Select a project to view uniform requests.</div>;
  }

  if (gate.kind === 'denied') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-6 text-sm text-amber-900" role="status">
        <div className="mb-1 flex items-center gap-2 font-semibold">
          <AlertTriangle className="h-4 w-4" />
          Access needed
        </div>
        {/* The server's own words when it sent any: "no grant on this project" and
            "could not verify who you are" are both a 403, and call for different
            things from the reader. */}
        <p>{gate.message ?? ACCESS_NEEDED}</p>
        {/* A grant takes effect on the next request, so let the person who has just
            been given it find out without leaving the tab. */}
        <button
          type="button"
          onClick={recheck}
          className="mt-3 text-sm font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
        >
          Check again
        </button>
      </div>
    );
  }

  if (gate.kind === 'not_configured') {
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-5 py-6 text-sm text-gray-700" role="status">
        <div className="mb-1 flex items-center gap-2 font-semibold text-gray-900">
          <Info className="h-4 w-4 text-gray-400" />
          Not set up on this server
        </div>
        <p>
          Uniform &amp; Asset Management is not set up on this server yet, so there is nothing to show. This is a
          server setting — not something your role or this project controls.
        </p>
        <p className="mt-1 text-xs text-gray-500">{gate.message}</p>
        <button
          type="button"
          onClick={recheck}
          className="mt-3 text-sm font-medium text-primary-600 hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const snapshot = snapshots[activeTab] ?? null;
  const tabError = tabErrors[activeTab] ?? null;
  const rows = snapshot?.rows ?? NO_ROWS;
  const shortfall = rowShortfall(activeTab, snapshot, counts);
  // Of the newest answer, like the counts it is printed beside.
  const snapshotTime = fmtSnapshotTime(latest?.fetchedAt);
  const garments = kitStock?.items.length ?? 0;
  // The five card figures, as text: a number, '…' while loading, '—' when there is none.
  const pct = slaPct(sla, slaLoading);
  const within = slaValue(sla, slaLoading, sla?.within);
  const out = slaValue(sla, slaLoading, sla?.out);
  const low = stockValue(kitStockLoading, kitStock?.totals.low);
  const zero = stockValue(kitStockLoading, kitStock?.totals.zero);

  return (
    <div className="space-y-4">
      {/* ─── As of ─── */}
      {/* The dashboard's name is the page title (DashboardHeader), and "Always live" under
          it says Month and Filters do not apply. So no paragraph here: only when the
          numbers were read, small and grey, with what they are on hover. */}
      {snapshotTime && (
        <p className="text-xs text-gray-400" title={UNIFORM_SOURCE_HINT}>As of {snapshotTime}</p>
      )}

      {/* ─── SLA + Kit stock ─── */}
      {/* The definitions ride on the hover of each card (the Info mark says there is
          one): they are long, and identical to 1Office's own. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <section aria-label="SLA" className={CARD} title={slaHint(sla)}>
          <div className="flex items-center justify-between gap-2">
            {/* No month control: the counts are always the current month, named here
                so they never read as all-time or as the Dashboard's selected month. */}
            <h3 className={TILE_LABEL}>SLA ({monthName(sla?.month?.key)})</h3>
            <Info className="h-3.5 w-3.5 flex-shrink-0 text-gray-300" aria-hidden />
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className={`${TILE_NUMBER} ${figureTone(pct, 'text-gray-900')}`}>{pct}</span>
            <span className="text-xs text-gray-500">Timely Closure</span>
          </div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-4">
            <span className="whitespace-nowrap">
              <span className={`text-lg font-semibold ${figureTone(within, 'text-emerald-600')}`}>{within}</span>
              <span className="ml-1 text-xs text-gray-500">Within SLA</span>
            </span>
            <span className="whitespace-nowrap">
              <span className={`text-lg font-semibold ${figureTone(out, 'text-rose-600')}`}>{out}</span>
              <span className="ml-1 text-xs text-gray-500">Out of TAT</span>
            </span>
          </div>
          {slaError ? (
            <p className="mt-1 text-xs text-amber-700">SLA counts unavailable: {slaError}</p>
          ) : sla && !slaLoading && !sla.measured && (
            <p className="mt-1 text-xs text-gray-400">Not measured — the KPI starts {fmtYmd(sla.since)}</p>
          )}
        </section>

        <section aria-label="Kit stock" className={CARD} title={KIT_STOCK_HINT}>
          <div className="flex items-center justify-between gap-2">
            <h3 className={TILE_LABEL}>Kit stock (all warehouses)</h3>
            <Info className="h-3.5 w-3.5 flex-shrink-0 text-gray-300" aria-hidden />
          </div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-4">
            <span className="whitespace-nowrap">
              <span className={`${TILE_NUMBER} ${figureTone(low, 'text-amber-600')}`}>{low}</span>
              <span className="ml-1 text-xs text-gray-500">Low</span>
            </span>
            <span className="whitespace-nowrap">
              <span className={`${TILE_NUMBER} ${figureTone(zero, 'text-rose-600')}`}>{zero}</span>
              <span className="ml-1 text-xs text-gray-500">Out of stock</span>
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between gap-2">
            {kitStockError ? (
              <p className="min-w-0 text-xs text-amber-700">Kit stock unavailable: {kitStockError}</p>
            ) : (
              <p className="min-w-0 text-xs text-gray-400">
                {!kitStock || kitStockLoading
                  ? ' '
                  : garments === 0
                    ? 'No uniform kit is attached to this project\'s budgets.'
                    : `Sizes, across ${fmtInt(garments)} garment${garments === 1 ? '' : 's'}`}
              </p>
            )}
            {/* Nothing to download while loading, after a failure, or for a project
                with no kit — the workbook would be a header row. */}
            <button
              type="button"
              onClick={() => void runDownload('kit-stock')}
              disabled={kitStockLoading || garments === 0 || !!busyDownloads['kit-stock']}
              title="Download every garment's stock, by size"
              className={PILL}
            >
              <Download className="h-3.5 w-3.5" /> {busyDownloads['kit-stock'] ? 'Preparing…' : 'Download'}
            </button>
          </div>
        </section>
      </div>

      {/* ─── Sub-tabs ─── */}
      {/* Each counts Pre Joinee (candidates) and Joined (employees) apart, on a
          second line under its name — from `counts`, never from the rows. */}
      <div role="tablist" aria-label="Uniform request stage" className="flex flex-wrap gap-1 border-b border-gray-200">
        {UNIFORM_TABS.map((t) => {
          const on = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => selectTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-2.5 text-left text-sm font-semibold transition-colors ${
                on ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="block">{t.label}</span>
              <span className="block text-xs font-normal">{countLabel(counts, t.id)}</span>
            </button>
          );
        })}
      </div>

      {tabError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{tabError}</span>
        </div>
      )}
      {actionError && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="shrink-0 rounded p-0.5 text-red-500 hover:bg-red-100 hover:text-red-700"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {notice && (
        // break-all: when the clipboard refused, this carries a whole URL.
        <div className="flex items-start justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" role="status">
          <span className="break-all">{notice}</span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="shrink-0 rounded p-0.5 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-800"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {/* Whenever fewer rows than the sub-tab's true count are on screen — whether
          or not upstream called that half capped. */}
      {shortfall && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          {shortfallText(shortfall)}
        </div>
      )}

      {/* ─── Table ─── */}
      <UniformRequestsTable
        // Remount per project + sub-tab: search, sort and page start fresh.
        key={`${projectId}:${activeTab}`}
        tab={activeTab}
        rows={rows}
        total={counts ? counts[activeTab].total : null}
        loading={!snapshot && !tabError}
        failed={!!tabError}
        linkBusy={linkBusy}
        onViewKit={setViewRow}
        onCopyLink={(r) => void copyLink(r)}
        onTrack={setTrackRow}
        toolbar={(
          <>
            <button
              type="button"
              onClick={() => void runDownload('report')}
              disabled={!!busyDownloads.report}
              title="One row per person, with their uniform request and status"
              className={PILL}
            >
              <Download className="h-3.5 w-3.5" /> {busyDownloads.report ? 'Preparing…' : 'Download Report'}
            </button>
            <button
              type="button"
              onClick={() => void runDownload('kit-report')}
              disabled={!!busyDownloads['kit-report']}
              title="Every completed request: who was given which kit, and each garment with its size and quantity"
              className={PILL}
            >
              <Download className="h-3.5 w-3.5" /> {busyDownloads['kit-report'] ? 'Preparing…' : 'Kit Report'}
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={loadingAll}
              title="Fetch the latest from 1Office"
              className={PILL}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loadingAll ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </>
        )}
      />

      {viewRow && (
        <UniformKitModal projectId={projectId} row={viewRow} onClose={() => setViewRow(null)} onDenied={enterGate} />
      )}
      {trackRow && (
        <UniformCourierStatusModal
          projectId={projectId}
          requestId={trackRow.request_id}
          requestCode={trackRow.request_code}
          onClose={() => setTrackRow(null)}
          onDenied={enterGate}
        />
      )}
    </div>
  );
}
